
import { Vue, Component, Prop } from 'vue-property-decorator'
import moment from 'moment'
import 'moment/locale/nl'
import axios from 'axios'
import Building, {
  buildingPhaseStates,
  constructionTypes,
  objectTypes,
  productTypes,
  saleStates
} from '@/types/building'
import { notification } from 'ant-design-vue'
import BuildingBulkCustomerUpdater from '@/components/listview/building/BuildingBulkCustomerUpdater.vue'
import BuildingBulkDeliveryCreate from '@/components/listview/building/BuildingBulkDeliveryCreate.vue'
import ls from '@/ls'
import { unset } from 'lodash'
import { can } from '@/globalFunctions'
moment.locale('nl')

@Component({
  methods: {
    can,
    buildingPhaseStates () {
      return buildingPhaseStates
    },
    saleStates () {
      return saleStates
    },
    constructionTypes () {
      return constructionTypes
    },
    objectTypes () {
      return objectTypes
    },
    productTypes () {
      return productTypes
    }
  },
  components: { BuildingBulkDeliveryCreate, BuildingBulkCustomerUpdater }
})

export default class BuildingBulkUpdater extends Vue {
  @Prop() selectedIds!:number[]
  @Prop() selectedBuildings!:Building[]

  saving = false
  downloading = { buildingSpecification: false, inspection: false, pvo: false, contacts: false }
  loading = { syncWithAfas: false }

  showUpdateModal = false
  currentTab = 'details'

  bulkUpdate:Record<string, (string|number|boolean|moment.Moment|null)> = {
    construction_type_id: null,
    product_type: null,
    object_type: null,
    state: null,
    zipcode: null,
    city: null,
    end_modular: null,
    status_modular: null,
    end_carcass: null,
    status_carcass: null,
    end_completion: null,
    status_completion: null,
    start_construction_date: null,
    transport_date: null,
    ready_for_invoicing: null,
    last_installment_paid: null,
    customer_id: null,
    unity_enabled: false
  }

  dateKeys:string[] = ['end_modular', 'end_carcass', 'end_completion', 'start_construction_date', 'transport_date']

  viewerStateIndeterminate = false
  viewerStateAllEnabled = false

  onUnityEnabledChange (e:any) : void {
    this.viewerStateIndeterminate = false
    this.viewerStateAllEnabled = e.target.checked
    this.bulkUpdate.unity_enabled = e.target.checked
  }

  checkUnityEnabledState () : void {
    const states = this.selectedBuildings.map(b => b.unity_enabled)
    this.viewerStateIndeterminate = states.includes(true) && states.includes(false)
    this.viewerStateAllEnabled = this.selectedBuildings.every(b => b.unity_enabled)
  }

  toggleUpdateModal () : void {
    this.resetBulkUpdateValues()
    this.showUpdateModal = !this.showUpdateModal
    this.currentTab = 'details'

    if (this.showUpdateModal) {
      this.checkUnityEnabledState()
    }
  }

  update () : void {
    this.saving = true
    const data: Record<string, string | number | boolean | null> = {}

    Object.keys(this.bulkUpdate).forEach(key => {
      if (key === 'unity_enabled' && this.viewerStateIndeterminate) {
        unset(this.bulkUpdate, key)
      }
      const value = this.bulkUpdate[key]

      if (value !== null && value !== '' && value !== undefined) {
        if (this.dateKeys.includes(key) && moment.isMoment(value)) {
          data[key] = moment(value).format('YYYY-MM-DD HH:mm:ss')
        } else {
          data[key] = value as string | number | boolean | null
        }
      }
    })

    if (Object.keys(data).length !== 0) {
      axios.post(process.env.VUE_APP_API_URL + '/buildings/bulk', { ids: this.selectedIds, data: data }).then(() => {
        this.$emit('updated')
        this.toggleUpdateModal()
        notification.success({ message: 'Bouwnummers bijgewerkt.', description: 'De bouwnummers zijn succesvol opgeslagen.', duration: 3 })
        this.resetBulkUpdateValues()
      }).finally(() => {
        this.saving = false
        this.resetBulkUpdateValues()
      })
    } else {
      this.saving = false
      this.toggleUpdateModal()
      this.resetBulkUpdateValues()
    }
  }

  bulkValueChanged (key:string, value:string|number|undefined) : void {
    if (value === undefined || value === '') {
      this.bulkUpdate[key] = null
    }
  }

  resetBulkUpdateValues () : void {
    this.bulkUpdate = {
      construction_type_id: null,
      product_type: null,
      object_type: null,
      state: null,
      zipcode: null,
      city: null,
      end_modular: null,
      status_modular: null,
      end_carcass: null,
      status_carcass: null,
      end_completion: null,
      status_completion: null,
      start_construction_date: null,
      transport_date: null,
      ready_for_invoicing: null,
      last_installment_paid: null,
      customer_id: null,
      unity_enabled: false
    }
  }

  downloadBuildingSpecifications () : void {
    this.downloading.buildingSpecification = true
    this.selectedBuildings.forEach((building:Building) => {
      axios.get(process.env.VUE_APP_API_URL + '/buildings/' + building.id + '/pdf/specification', { responseType: 'arraybuffer' })
        .then(res => {
          const blob = new Blob([res.data], { type: 'application/pdf' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'Meterkastlijst_P' + building.project?.reference + '_B' + building.number + '.pdf'
          link.click()
        }).finally(() => {
          this.downloading.buildingSpecification = false
        })
    })
  }

  downloadInspections () : void {
    this.downloading.inspection = true
    axios.post(process.env.VUE_APP_API_URL + '/buildings/bulk/download/inspection/pdf', { building_ids: this.selectedIds }, { responseType: 'arraybuffer' }).then((r) => {
      const blob = new Blob([r.data], { type: 'application/zip' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = 'Export_voorschouw_' + new Date().toLocaleDateString('nl-NL') + '.zip'
      link.click()
    }).finally(() => {
      this.downloading.inspection = false
    })
  }

  downloadPvos () : void {
    this.downloading.pvo = true
    axios.post(process.env.VUE_APP_API_URL + '/buildings/bulk/download/pvo/pdf', { building_ids: this.selectedIds }, { responseType: 'arraybuffer' }).then((r) => {
      const blob = new Blob([r.data], { type: 'application/zip' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = 'Export_pvo_' + new Date().toLocaleDateString('nl-NL') + '.zip'
      link.click()
    }).finally(() => {
      this.downloading.pvo = false
    })
  }

  downloadContacts () : void {
    this.downloading.contacts = true
    axios.post(process.env.VUE_APP_API_URL + '/reports/buildings/contacts', { buildingIds: this.selectedIds }, { responseType: 'arraybuffer' }).then((r) => {
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = 'Export_contactgegevens_' + this.currentDateTime() + '.xlsx'
      link.click()
    }).finally(() => {
      this.downloading.contacts = false
    })
  }

  syncWithAfas () : void {
    this.loading.syncWithAfas = true
    axios.post(process.env.VUE_APP_API_URL + '/buildings/bulk/syncWithAfas', { buildingIds: this.selectedIds }).then(() => {
      this.$emit('updated')
    }).finally(() => {
      this.loading.syncWithAfas = false
    })
  }

  currentDateTime () : string {
    const now = new Date()
    const day = String(now.getDate()).padStart(2, '0')
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const year = now.getFullYear()
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    return `${day}-${month}-${year}_${hours}${minutes}`
  }

  deliveriesScheduled () : void {
    this.toggleUpdateModal()
    this.$emit('updated')
  }

  moment (dateTime:string, format:string) : moment.Moment {
    return moment(dateTime, format)
  }

  get hasChanges () : { details:boolean, lot:boolean, dates:boolean, financial:boolean, buyer:boolean } {
    const { bulkUpdate } = this

    return {
      details: [bulkUpdate.product_type, bulkUpdate.object_type, bulkUpdate.state].some(value => value !== null),
      lot: [bulkUpdate.zipcode, bulkUpdate.city].some(value => value !== null),
      dates: [
        bulkUpdate.end_modular, bulkUpdate.status_modular,
        bulkUpdate.end_carcass, bulkUpdate.status_carcass,
        bulkUpdate.end_completion, bulkUpdate.status_completion
      ].some(value => value !== null),
      financial: bulkUpdate.ready_for_invoicing !== null || bulkUpdate.last_installment_paid !== null,
      buyer: bulkUpdate.customer_id !== null
    }
  }
}
